/**
 * Frontend self-detection: window-level error + unhandled-rejection capture
 * + React ErrorBoundary. POSTs sanitized payloads to /api/system/error-report,
 * which emails the developer (rate-limited, PHI-redacted server-side).
 *
 * Usage:
 *   import { installGlobalErrorReporter, ErrorBoundary } from './lib/errorReporter';
 *   installGlobalErrorReporter();
 *   <ErrorBoundary> <App /> </ErrorBoundary>
 */
import React from 'react';
import { getDesktopToken } from './desktopToken';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const ENDPOINT = `${BACKEND_URL}/api/system/error-report`;

// Local de-dup so we don't blast the network when one bug fires in a loop.
// Keyed by `${source}|${message.slice(0,200)}|${stackTopFrame}`.
const sentFingerprints = new Map(); // fingerprint -> last-sent ts
const LOCAL_THROTTLE_MS = 60_000; // 1 min — server has its own 30-min throttle

function fingerprintFor(source, message, stack) {
  const head = (stack || '').split('\n').slice(0, 3).join('|');
  return `${source}|${(message || '').slice(0, 200)}|${head}`;
}

async function postReport(payload) {
  try {
    const fp = fingerprintFor(payload.source, payload.message, payload.stack);
    const now = Date.now();
    const last = sentFingerprints.get(fp);
    if (last && now - last < LOCAL_THROTTLE_MS) return;
    sentFingerprints.set(fp, now);

    // Auth header is optional — endpoint accepts unauthenticated reports.
    // Browsers send the httpOnly cookie automatically; only the desktop
    // terminal has a token to attach.
    const token = getDesktopToken();
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;

    // Use sendBeacon when available so the report fires even on tab close,
    // but fall back to fetch with keepalive so we still get the auth header.
    const body = JSON.stringify(payload);
    if (!token && navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      navigator.sendBeacon(ENDPOINT, blob);
      return;
    }
    fetch(ENDPOINT, {
      method: 'POST',
      headers,
      body,
      keepalive: true,
      credentials: 'include',
    }).catch(() => {});
  } catch (e) {
    // Never let the reporter crash the app
    console.warn('errorReporter: failed to post', e);
  }
}

function basePayload() {
  return {
    url: typeof window !== 'undefined' ? window.location.href : '',
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
  };
}

export function installGlobalErrorReporter() {
  if (typeof window === 'undefined') return;
  if (window.__pharmxaiErrorReporterInstalled) return;
  window.__pharmxaiErrorReporterInstalled = true;

  // Synchronous JS errors
  window.addEventListener('error', (event) => {
    if (!event) return;
    // Asset-load failures (img/script onerror) bubble here without an Error
    // instance — skip them; only report scripting bugs.
    const err = event.error;
    if (!err && !event.message) return;
    postReport({
      source: 'frontend.window',
      level: 'error',
      message: (err && err.message) || event.message || 'Unknown error',
      stack: (err && err.stack) || '',
      ...basePayload(),
      extra: {
        filename: event.filename,
        line: event.lineno,
        column: event.colno,
      },
    });
  });

  // Unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const message = (reason && (reason.message || String(reason))) || 'Unhandled rejection';
    const stack = (reason && reason.stack) || '';
    postReport({
      source: 'frontend.unhandledrejection',
      level: 'error',
      message,
      stack,
      ...basePayload(),
    });
  });
}

/**
 * React error boundary — catches rendering errors anywhere in the tree
 * and reports them, then shows a fallback UI.
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: '' };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, message: (error && error.message) || 'Render error' };
  }

  componentDidCatch(error, info) {
    postReport({
      source: 'frontend.react',
      level: 'critical',
      message: (error && error.message) || 'React render error',
      stack: (error && error.stack) || '',
      component_stack: (info && info.componentStack) || '',
      ...basePayload(),
    });
  }

  handleReload = () => {
    this.setState({ hasError: false, message: '' });
    if (typeof window !== 'undefined') window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div
        data-testid="frontend-error-boundary"
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f8fafc',
          padding: '24px',
          fontFamily: 'Segoe UI, Arial, sans-serif',
        }}
      >
        <div
          style={{
            maxWidth: 480,
            background: '#fff',
            border: '1px solid #fecaca',
            borderRadius: 12,
            padding: '28px 32px',
            boxShadow: '0 8px 24px rgba(0,0,0,.06)',
          }}
        >
          <div style={{ fontSize: 38, marginBottom: 8 }}>⚠️</div>
          <h2 style={{ margin: '0 0 8px', color: '#0a4d3c' }}>
            Something went wrong
          </h2>
          <p style={{ color: '#64748b', margin: '0 0 16px', lineHeight: 1.5 }}>
            We&apos;ve sent the error to the developer automatically. You can
            reload the page to keep working.
          </p>
          <button
            type="button"
            onClick={this.handleReload}
            data-testid="error-boundary-reload"
            style={{
              padding: '10px 18px',
              background: '#0a4d3c',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            Reload app
          </button>
        </div>
      </div>
    );
  }
}

/** Optional helper — call from anywhere to log a captured error to the dev. */
export function reportClientError(message, error, extra) {
  postReport({
    source: 'frontend.api',
    level: 'error',
    message: message || (error && error.message) || 'Client error',
    stack: (error && error.stack) || '',
    ...basePayload(),
    extra: extra || {},
  });
}
